import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Quote, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUGGEST = {
  cpg: ["Reach out to one new stockist", "Post about your origin story", "Write three possible taglines for your brand", "Ask a stockist what their customers say about you",
    "Photograph your product where it's sold", "Email your list one recipe or serve idea", "Check your shelf price against two competitors", "Send a thank-you note to your best stockist",
    "Film 15 seconds of how your product is made"],
  hospitality: ["Message a regular to say thank you", "Post a behind-the-scenes from today's prep", "Review last month's most popular dish and think about what it tells you",
    "Reply to every review from this week", "Ask your team for one idea to fill a quiet night", "Update your Google profile photos", "Introduce a team member on social",
    "Collect five email sign-ups at the table", "Plan one reason to visit next week"],
};
const QUOTES = [
  "Innocent started in 1999 at a London music festival, asking drinkers to bin their cups under 'Yes' or 'No' to decide whether to quit their jobs. 'Yes' won.",
  "Most first-time guests don't come back because they were never invited back. A follow-up beats a discount.",
  "A buyer isn't buying your product. They're buying its rate of sale.",
  "Consistency beats creativity on social. A good post every week wins against a great post every month.",
  "Your regulars are your best marketing team. Give them a reason to talk about you.",
  "Positioning is choosing who you're not for.",
  "Getting listed is the start line, not the finish line. Support the sell-through.",
  "The best restaurant marketing happens on the plate, then gets told well online.",
  "Price is a story too. Cheap tells one, premium tells another, so make sure it matches your brand.",
  "Sampling is the oldest marketing trick in food for a reason: taste converts.",
  "An email list is the only audience you truly own.",
  "Seasonal planning starts 8–12 weeks before the date, not the week before.",
  "Your Google profile is often your real homepage. Keep it fresh.",
  "Founders who show their face online build trust faster than logos ever can.",
  "One clear offer beats five confusing ones.",
  "Ask 'why did you choose us?' and use their exact words in your marketing.",
  "Margin is a marketing decision. Discounts train customers to wait.",
  "A great collab borrows an audience. A weak one just swaps logos.",
  "Retailers remember the brands that make their job easier.",
  "Repeat customers usually cost far less to win than new ones. Look after them first.",
  "Behind-the-scenes content works because people buy from people.",
];
const dayIdx = () => Math.floor(Date.now() / 86400000);

export const DailyCard = () => {
  const { user } = useAuth();
  const list = user?.audience === "cpg" ? SUGGEST.cpg : user?.audience === "hospitality" ? SUGGEST.hospitality
    : SUGGEST.cpg.map((x, i) => [x, SUGGEST.hospitality[i]]).flat();
  const d = dayIdx();
  const picks = [0, 1, 2].map((i) => list[(d * 3 + i) % list.length]);
  return (
    <div className="mb-5 grid gap-4 lg:grid-cols-5" data-testid="daily-card">
      <div className="lg:col-span-3 rounded-2xl bg-sun/15 border border-sun/40 p-6">
        <div className="flex items-center gap-2 eyebrow mb-2"><Sun className="h-4 w-4 text-pinkb" />Today</div>
        <p className="font-serif text-xl text-navy" data-testid="daily-prompt">What one small thing are you excited to do for your brand today?</p>
        <ul className="mt-3 space-y-1.5 text-sm">{picks.map((p, i) => <li key={p} data-testid={`daily-suggestion-${i}`} className="flex gap-2"><span className="text-pinkb">→</span>{p}</li>)}</ul>
      </div>
      <div className="lg:col-span-2 rounded-2xl bg-navy text-white p-6" data-testid="daily-quote">
        <Quote className="h-5 w-5 text-sun" /><p className="mt-2 text-sm leading-relaxed">{QUOTES[d % QUOTES.length]}</p>
      </div>
    </div>
  );
};

export const ProgressTracker = () => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me/progress").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  const left = p.total - p.done;
  const nudge = left === 0 ? "Every milestone ticked. You're flying. Keep the weekly rhythm going."
    : p.this_week >= 3 ? `You're doing great: ${p.this_week} actions this week.`
    : left === 1 ? "One more thing and you've ticked off every milestone."
    : p.this_week > 0 ? `Nice start: ${p.this_week} action${p.this_week === 1 ? "" : "s"} this week. One more makes it a proper week of progress.`
    : "Small and done beats big and planned. Pick one thing below to start.";
  return (
    <div className="mb-5 rounded-2xl border border-border bg-card p-6" data-testid="progress-tracker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif uppercase text-lg text-navy">Your progress</h2>
        <span className="font-serif text-2xl text-pinkb" data-testid="progress-percent">{p.percent}%</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-leaf transition-all duration-700" style={{ width: `${p.percent}%` }} /></div>
      <p className="mt-3 text-sm text-navy" data-testid="progress-nudge">{nudge}</p>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">{p.milestones.map((m) => (
        <li key={m.key} className="flex items-center gap-2" data-testid={`milestone-${m.key}`}>{m.done ? <CheckCircle2 className="h-4 w-4 text-leaf" /> : <Circle className="h-4 w-4 text-muted-foreground" />}<span className={m.done ? "" : "text-muted-foreground"}>{m.label}</span></li>
      ))}</ul>
    </div>
  );
};
